import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Users, FileText, Grid, UserCheck, BarChart3, Briefcase, Settings, X, ArrowLeftRight, CalendarDays, Network, Target, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { transactionsService } from '../services/transactions';
import { getCurrentUser, hasAnyPermission, hasPermission } from '../utils/roles';

const iconMap = {
  Home, Users, FileText, Grid, UserCheck, BarChart3,
  Briefcase, Settings, ArrowLeftRight, CalendarDays, Network,
  Target,
};

const menuItems = [
  { key: 'dashboard',    icon: 'Home',           path: '/dashboard', permission: 'View Dashboard' },
  { key: 'clients',      icon: 'Users',          path: '/clients', permission: 'View Clients' },
  { key: 'transactions', icon: 'ArrowLeftRight',  path: '/transactions', permission: 'View Transactions' },
  { key: 'targets',      icon: 'Target',          path: '/targets', permission: 'View Finance' },
  { key: 'documents',    icon: 'FileText',        path: '/documents', permission: 'View Documents' },
  { key: 'calendar',     icon: 'CalendarDays',    path: '/calendar', permission: 'View Calendar' },
  { key: 'offices',      icon: 'Grid',            path: '/offices', permission: 'View Offices' },
  { key: 'hierarchy',    icon: 'Network',          path: '/hierarchy', any: ['View Users', 'View Offices'] },
  { key: 'analytics',    icon: 'BarChart3',       path: '/analytics', permission: 'View Analytics' },
  { key: 'affiliates',   icon: 'Briefcase',       path: '/affiliates', permission: 'View Affiliates' },
  { key: 'settings',     icon: 'Settings',        path: '/settings', permission: 'View Settings' },
];

const Sidebar = ({ isOpen, onClose, isCollapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const { t } = useLanguage();
  const currentUser = getCurrentUser();
  const [moneyPendingCount, setMoneyPendingCount] = useState(0);
  const visibleMenuItems = menuItems.filter(item => {
    if (item.permission) return hasPermission(currentUser, item.permission);
    if (item.any) return hasAnyPermission(currentUser, item.any);
    return true;
  });
  const canViewFinance = hasPermission(currentUser, 'View Finance');

  useEffect(() => {
    if (!canViewFinance) return undefined;
    let cancelled = false;
    const loadMoneyPendingCount = async () => {
      try {
        const data = await transactionsService.getTransactions(1, 1, { status: 'pending' });
        if (!cancelled) setMoneyPendingCount(data.total || 0);
      } catch {
        if (!cancelled) setMoneyPendingCount(0);
      }
    };
    const handleMoneyPendingChanged = (event) => {
      if (typeof event.detail?.total === 'number') {
        setMoneyPendingCount(event.detail.total);
      } else {
        loadMoneyPendingCount();
      }
    };
    loadMoneyPendingCount();
    window.addEventListener('money-pending-count-changed', handleMoneyPendingChanged);
    window.addEventListener('focus', loadMoneyPendingCount);
    return () => {
      cancelled = true;
      window.removeEventListener('money-pending-count-changed', handleMoneyPendingChanged);
      window.removeEventListener('focus', loadMoneyPendingCount);
    };
  }, [canViewFinance]);

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-40 lg:hidden"
          style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:sticky top-0 left-0 h-screen flex-shrink-0
          transition-all duration-300 ease-out z-50 w-64 ${isCollapsed ? 'lg:w-20' : 'lg:w-64'}
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{
          background: 'linear-gradient(180deg, #05070e 0%, #07090f 100%)',
          borderRight: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div className="flex flex-col h-full relative">
          {/* ── Logo ────────────────────────────────────── */}
          <div
            className={`flex items-center py-5 ${isCollapsed ? 'px-4 lg:px-3 lg:justify-center' : 'px-5'}`}
            style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div className={`flex items-center min-w-0 ${isCollapsed ? 'lg:justify-center lg:flex-1' : ''}`}>
              <img
                src={`${process.env.PUBLIC_URL}/logo.webp`}
                alt="Abramov CRM"
                className={`h-12 w-40 object-contain object-left ${isCollapsed ? 'lg:hidden' : ''}`}
              />
              {isCollapsed && (
                <img
                  src={`${process.env.PUBLIC_URL}/favicon-32x32.png`}
                  alt="Abramov CRM"
                  className="hidden lg:block h-8 w-8 object-contain"
                />
              )}
            </div>
            <button
              onClick={onClose}
              title="Close menu"
              aria-label="Close menu"
              className="lg:hidden w-7 h-7 rounded-lg flex items-center justify-center text-gray-500 hover:text-white transition-all duration-200"
              style={{ background: 'rgba(255,255,255,0.05)' }}
            >
              <X size={14} />
            </button>
          </div>

          {/* ── Nav label ───────────────────────────────── */}
          <div className={`px-5 pt-5 pb-2 ${isCollapsed ? 'lg:hidden' : ''}`}>
            <span className="text-xs font-semibold tracking-[0.15em] uppercase" style={{ color: 'rgba(255,255,255,0.2)' }}>
              {t('nav.menu')}
            </span>
          </div>

          {/* ── Navigation ──────────────────────────────── */}
          <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-0.5">
            {visibleMenuItems.map((item) => {
              const Icon = iconMap[item.icon];
              const isActive = location.pathname === item.path;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => onClose()}
                  title={isCollapsed ? t(`nav.${item.key}`) : undefined}
                  className={`relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium group transition-all duration-200 ${isCollapsed ? 'lg:justify-center lg:px-2' : ''}`}
                  style={isActive ? {
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(139,92,246,0.08) 100%)',
                    color: '#fff',
                  } : {
                    color: 'rgba(255,255,255,0.62)',
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                      e.currentTarget.style.color = 'rgba(255,255,255,0.9)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = 'rgba(255,255,255,0.62)';
                    }
                  }}
                >
                  {/* Active left indicator */}
                  {isActive && (
                    <span
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 rounded-r-full"
                      style={{ background: 'linear-gradient(180deg, #818cf8 0%, #a78bfa 100%)' }}
                    />
                  )}

                  {/* Icon */}
                  <span
                    className="flex-shrink-0"
                    style={isActive ? {
                      color: '#818cf8',
                      filter: 'drop-shadow(0 0 6px rgba(129,140,248,0.6))',
                    } : {}}
                  >
                    <Icon size={17} />
                  </span>

                  <span className={`flex-1 truncate ${isCollapsed ? 'lg:hidden' : ''}`}>{t(`nav.${item.key}`)}</span>
                  {item.key === 'transactions' && moneyPendingCount > 0 && canViewFinance && (
                    <span className={`min-w-5 rounded-full bg-yellow-500/20 px-1.5 py-0.5 text-center text-[10px] font-bold leading-none text-yellow-400 ${isCollapsed ? 'lg:absolute lg:right-1 lg:top-1' : ''}`}>
                      {moneyPendingCount}
                    </span>
                  )}

                  {/* Active dot */}
                  {isActive && !(item.key === 'transactions' && moneyPendingCount > 0 && canViewFinance) && (
                    <span
                      className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isCollapsed ? 'lg:hidden' : ''}`}
                      style={{
                        background: '#818cf8',
                        boxShadow: '0 0 6px rgba(129,140,248,0.9)',
                        animation: 'pulse-glow 2s ease-in-out infinite',
                      }}
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          <div
            className={`hidden lg:flex px-3 py-4 ${isCollapsed ? 'justify-center' : 'justify-end'}`}
            style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
          >
            <button
              onClick={onToggleCollapse}
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-500 hover:text-white transition-all duration-200"
              style={{ background: 'rgba(255,255,255,0.05)' }}
            >
              {isCollapsed ? <ChevronsRight size={15} /> : <ChevronsLeft size={15} />}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
